<template>
  <div class="page home">
    <PageHeader :title="greeting" :subtitle="subtitle" icon="mdi-view-dashboard-outline" tall>
      <template #actions>
        <v-btn v-if="canCreateRequest" class="btn-leaf" :to="{ path: '/requests', query: { nou: '1' } }">
          <v-icon left>mdi-plus</v-icon>Demers nou
        </v-btn>
      </template>
    </PageHeader>

    <div class="page-body">
      <div class="tiles" :class="'tiles--' + tiles.length">
        <router-link v-for="t in tiles" :key="t.key" :to="t.to" class="tile" :class="'tile--' + t.tone">
          <v-icon class="tile__icon" aria-hidden="true">{{ t.icon }}</v-icon>
          <span class="tile__value">
            <v-progress-circular v-if="loading" indeterminate size="34" width="3" />
            <template v-else>{{ t.value }}</template>
          </span>
          <span class="tile__label">{{ t.label }}</span>
          <span class="tile__note">{{ t.note }}</span>
        </router-link>
      </div>

      <div class="home-grid">
        <div class="home-main">
          <section v-if="showQueue" class="panel">
            <div class="panel-toolbar">
              <h2 class="panel-title">{{ isEngineerRole ? "De preluat și în lucru" : "Așteaptă aprobarea dvs." }} <span class="count">{{ queue.length }}</span></h2>
              <span class="grow-spacer" />
              <v-btn text color="primary" to="/approval">Deschide Aprobare</v-btn>
            </div>
            <ul class="req-list">
              <li v-for="item in queue.slice(0, 5)" :key="'q' + item.requestId">
                <router-link to="/approval" class="req">
                  <StatusChip :status="item.status" small />
                  <span class="req__title">{{ item.cause }}<span class="req__model">{{ item.model }}</span></span>
                  <span class="req__meta">{{ item.user }}<template v-if="item.department">, {{ item.department }}</template></span>
                  <span class="req__date">{{ shortDate(item.date) }}</span>
                </router-link>
              </li>
            </ul>
          </section>

          <section class="panel">
            <div class="panel-toolbar">
              <h2 class="panel-title">Ultimele demersuri</h2>
              <span class="grow-spacer" />
              <v-btn v-if="canCreateRequest" text color="primary" to="/requests">Toate demersurile</v-btn>
            </div>
            <div v-if="loading" class="empty"><v-progress-circular indeterminate color="primary" /></div>
            <div v-else-if="recent.length === 0" class="empty">
              <p>Nu există încă demersuri{{ hasManyDepartments ? " în departamentele dvs." : " în departamentul dvs." }}</p>
              <v-btn v-if="canCreateRequest" class="btn-green" :to="{ path: '/requests', query: { nou: '1' } }">Trimiteți primul demers</v-btn>
            </div>
            <ul v-else class="req-list">
              <li v-for="item in recent" :key="item.id">
                <router-link :to="{ path: '/history', query: { cerere: item.id } }" class="req">
                  <StatusChip :status="item.status" small />
                  <span class="req__title">{{ item.cause }}<span class="req__model">{{ item.model }}</span></span>
                  <span class="req__meta">{{ item.user }}<template v-if="hasManyDepartments || isStaff">, {{ item.userDepartment }}</template></span>
                  <span class="req__date">{{ shortDate(item.date) }}</span>
                </router-link>
              </li>
            </ul>
          </section>
        </div>

        <aside class="home-side">
          <section class="panel">
            <div class="panel-toolbar">
              <h2 class="panel-title">{{ departments.length + subunitGroups.length > 1 ? "Departamentele dvs." : "Departamentul dvs." }}</h2>
            </div>
            <ul class="dept-list">
              <li v-for="d in departments" :key="d.name">
                <span class="dept-list__name">{{ d.name }}</span>
                <span class="dept-list__tags">
                  <span v-if="d.isPrimary && departments.length > 1" class="tag">principal</span>
                  <span v-if="d.isSef" class="tag tag--sef">Șef</span>
                </span>
                <span v-if="d.isSef" class="dept-list__note">Aprobați demersurile acestui departament.</span>
              </li>
              <li v-for="g in subunitGroups" :key="'sub-' + g.from">
                <span class="dept-list__name">{{ g.names.join(", ") }}</span>
                <span class="dept-list__tags">
                  <span class="tag tag--sub">{{ g.names.length > 1 ? "subdiviziuni" : "subdiviziune" }}</span>
                </span>
                <span class="dept-list__note">
                  {{ g.names.length > 1 ? "Nu au" : "Nu are" }} Șef propriu: aprobați demersurile, ca Șef al „{{ g.from }}”.
                </span>
              </li>
              <li v-if="departments.length === 0" class="dept-list__note">Nu aveți un departament. Contactați DTI.</li>
            </ul>
            <p v-if="departments.length > 1" class="side-note">
              Vedeți demersurile și echipamentele tuturor departamentelor într-un singur cont. La un demers nou alegeți
              pentru care departament îl trimiteți.
            </p>
          </section>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/layout/PageHeader.vue";
import StatusChip from "@/components/elements/StatusChip.vue";
import * as helper from "@/helper/helper";
import { roleLabel, SEF_ROLES } from "@/config/roles";
import { fetchMyWork, hasWorkQueue, uniqueByRequest } from "./approval/queue";
import { fetchRequestsForRole, fetchEquipmentForRole, CLOSED_STATUSES } from "./requests/scope";

const REQUEST_ROLES = ["user", "admin", "inginer", "usersef", "dtisef", "rector", "contability", "inginer-pc"];

export default {
  name: "HomePage",
  components: { PageHeader, StatusChip },
  data() {
    return {
      loading: true,
      requests: [],
      equipment: [],
      queue: [],
    };
  },
  computed: {
    user() {
      return this.$store.getters.getUser || {};
    },
    departments() {
      return this.$store.getters.myDepartments;
    },
    subunits() {
      return this.$store.getters.sefSubunits.filter((s) => !this.departments.some((d) => d.name === s.name));
    },
    // one entry per department they head
    subunitGroups() {
      const groups = new Map();
      this.subunits.forEach((s) => groups.set(s.from, [...(groups.get(s.from) || []), s.name]));
      return [...groups].map(([from, names]) => ({ from, names }));
    },
    hasManyDepartments() {
      return this.$store.getters.hasManyDepartments;
    },
    isStaff() {
      return ["admin", "inginer", "inginer-pc", "dtisef", "rector", "contability"].includes(this.user.role);
    },
    canCreateRequest() {
      return REQUEST_ROLES.includes(this.user.role);
    },
    showQueue() {
      return hasWorkQueue(this.user.role) && this.queue.length > 0;
    },
    greeting() {
      const h = new Date().getHours();
      const hello = h < 11 ? "Bună dimineața" : h < 18 ? "Bună ziua" : "Bună seara";
      const name = [this.user.firstName, this.user.lastName].filter(Boolean).join(" ");
      return name ? `${hello}, ${name}` : hello;
    },
    subtitle() {
      const role = roleLabel(this.user.role);
      const sef = this.departments.filter((d) => d.isSef).map((d) => d.name);
      const member = this.departments.filter((d) => !d.isSef).map((d) => d.name);
      if (sef.length && SEF_ROLES.includes(this.user.role)) {
        return `Șef: ${sef.join(", ")}` + (member.length ? `. Membru: ${member.join(", ")}` : "");
      }
      // "Rector" / "Profesor" are both the role and the department name
      const names = this.departments.map((d) => d.name).filter((name) => name !== role);
      return [role, names.join(", ")].filter(Boolean).join(", ");
    },
    openRequests() {
      return this.requests.filter((r) => !CLOSED_STATUSES.includes(r.status));
    },
    doneThisMonth() {
      const now = new Date();
      return this.requests.filter((r) => {
        if (r.status !== "Îndeplinit" || !r.date) return false;
        const d = new Date(r.date.replace(" ", "T"));
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      });
    },
    // what the numbers cover — the same lists as the Demersuri / Echipamente pages (requests/scope.js)
    ownScope() {
      return this.hasManyDepartments ? "în departamentele dvs." : "în departamentul dvs.";
    },
    requestScopeNote() {
      if (["admin", "dtisef", "rector"].includes(this.user.role)) return "în toată universitatea";
      if (this.user.role === "inginer") return "imprimante, copiatoare, MFU";
      if (this.user.role === "inginer-pc") return "calculatoare și periferice";
      return this.ownScope;
    },
    equipmentScopeNote() {
      if (["admin", "inginer", "dtisef", "rector"].includes(this.user.role)) return "în toată universitatea";
      if (this.user.role === "inginer-pc") return "calculatoare și periferice";
      return this.ownScope;
    },
    // engineers, and the admins for the types they execute: their queue is work, not approvals
    isEngineerRole() {
      return this.$store.getters.doesEngineerWork;
    },
    tiles() {
      const tiles = [];
      // the admins only once Configurare gave them a type and something waits (otherwise a „0” tile forever)
      const ownWork = this.user.role === "admin" ? this.queue.length > 0 : hasWorkQueue(this.user.role);
      if (ownWork) {
        // an engineer's queue also holds the work they started
        const engineer = this.isEngineerRole;
        tiles.push({ key: "q", tone: "amber", icon: "mdi-stamper", value: this.queueCount, label: engineer ? "De executat" : "De aprobat", note: this.queueCount ? (engineer ? "de preluat sau în lucru" : "vă așteaptă decizia") : "nimic în așteptare", to: "/approval" });
      }
      if (this.canCreateRequest) {
        tiles.push({ key: "o", tone: "green", icon: "mdi-progress-clock", value: this.openRequests.length, label: "Demersuri în lucru", note: this.requestScopeNote, to: "/requests" });
        tiles.push({ key: "d", tone: "sky", icon: "mdi-check-decagram-outline", value: this.doneThisMonth.length, label: "Îndeplinite luna aceasta", note: this.monthName, to: "/history" });
        tiles.push({ key: "e", tone: "leaf", icon: "mdi-desktop-classic", value: this.equipment.length, label: "Echipamente", note: this.equipmentScopeNote, to: "/equipments" });
      }
      return tiles;
    },
    queueCount() {
      return this.queue.length;
    },
    monthName() {
      const m = new Date().toLocaleDateString("ro-RO", { month: "long", year: "numeric" });
      return m.charAt(0).toUpperCase() + m.slice(1);
    },
    recent() {
      return [...this.requests].sort((a, b) => b.id - a.id).slice(0, 6);
    },
  },
  methods: {
    shortDate(value) {
      if (!value) return "";
      const d = new Date(String(value).replace(" ", "T"));
      return isNaN(d) ? value : d.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    async load() {
      this.loading = true;
      const role = this.user.role;
      const jobs = [
        this.canCreateRequest ? fetchRequestsForRole(this.user) : Promise.resolve([]),
        this.canCreateRequest ? fetchEquipmentForRole(this.user) : Promise.resolve([]),
        hasWorkQueue(role) ? fetchMyWork(role) : Promise.resolve([]),
      ];
      const [requests, equipment, queue] = await Promise.allSettled(jobs);
      this.requests = requests.status === "fulfilled" ? requests.value : [];
      this.equipment = equipment.status === "fulfilled" ? equipment.value : [];
      const list = queue.status === "fulfilled" ? queue.value : [];
      // one row per request, newest first
      this.queue = uniqueByRequest(list).sort((a, b) => b.requestId - a.requestId);
      this.$store.commit("SET_APPROVAL_COUNT", this.queueCount);
      this.loading = false;
    },
  },
  mounted() {
    helper.requestNotificationPermission();
    helper.setupWebSocket(this, this.user.role);
    this.load();
  },
};
</script>

<style scoped>
.tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}
.tiles--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.tile {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 176px;
  padding: 20px 22px;
  border-radius: 20px;
  text-decoration: none;
  box-shadow: 0 16px 34px -18px rgba(10, 31, 24, 0.55);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 22px 40px -18px rgba(10, 31, 24, 0.6);
}
.tile__icon.v-icon {
  position: absolute;
  right: -10px;
  bottom: -18px;
  font-size: 118px;
  color: currentColor;
  opacity: 0.14;
}
.tile__value {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 3.6rem;
  line-height: 1;
  letter-spacing: -0.04em;
  min-height: 58px;
}
.tile__label {
  margin-top: auto;
  font-size: 1.12rem;
  font-weight: 700;
}
.tile__note {
  font-size: 0.95rem;
  opacity: 0.85;
}
.tile--amber {
  background: linear-gradient(140deg, #ffd88a 0%, #f0a534 100%);
  color: #3a2605 !important;
}
.tile--green {
  background: linear-gradient(140deg, #34996a 0%, #1a5a3b 100%);
  color: #fff !important;
}
.tile--sky {
  background: linear-gradient(140deg, #3a7fc4 0%, #1d4c80 100%);
  color: #fff !important;
}
.tile--leaf {
  background: linear-gradient(140deg, #d9f3e3 0%, #8fd8af 100%);
  color: var(--pine-900) !important;
}
html.density-compact .tile {
  min-height: 112px;
  padding: 14px 18px;
}
html.density-compact .tile__value {
  font-size: 2.6rem;
  min-height: 0;
}
html.density-compact .tile__icon.v-icon {
  font-size: 84px;
}
html.density-compact .req {
  padding: 8px 16px;
}
.home-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 20px;
  margin-top: 24px;
  align-items: start;
}
.home-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.req-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.req-list li + li {
  border-top: 1px solid var(--line-soft);
}
.req {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "title status"
    "meta date";
  column-gap: 16px;
  row-gap: 2px;
  align-items: center;
  padding: 14px 20px;
  color: var(--ink) !important;
  text-decoration: none;
}
.req:hover {
  background: var(--mint-50);
}
.req .status-chip {
  grid-area: status;
  justify-self: end;
}
.req__title {
  grid-area: title;
  font-weight: 700;
  font-size: 1.02rem;
  min-width: 0;
}
.req__model {
  font-weight: 400;
  color: var(--ink-2);
  margin-left: 8px;
}
.req__meta {
  grid-area: meta;
  color: var(--ink-3);
  font-size: 0.93rem;
}
.req__date {
  grid-area: date;
  justify-self: end;
  color: var(--ink-2);
  font-size: 0.93rem;
  font-variant-numeric: tabular-nums;
}
.empty {
  padding: 36px 20px;
  text-align: center;
  color: var(--ink-2);
}
.dept-list {
  list-style: none;
  margin: 0;
  padding: 6px 20px 10px;
}
.dept-list li {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 10px;
  padding: 12px 0;
}
.dept-list li + li {
  border-top: 1px solid var(--line-soft);
}
.dept-list__name {
  font-weight: 700;
}
.dept-list__tags {
  display: flex;
  gap: 6px;
  align-items: flex-start;
}
.dept-list__note {
  grid-column: 1 / -1;
  font-size: 0.92rem;
  color: var(--ink-3);
}
.tag {
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--slate-bg);
  color: var(--slate);
  font-size: 0.8rem;
  font-weight: 700;
}
.tag--sef {
  background: var(--pine-900);
  color: var(--leaf);
}
.tag--sub {
  background: var(--sky-bg);
  color: var(--sky);
}
.side-note {
  margin: 0;
  padding: 14px 20px 18px;
  border-top: 1px solid var(--line-soft);
  font-size: 0.93rem;
  line-height: 1.5;
  color: var(--ink-2);
}
@media (max-width: 1264px) {
  .tiles,
  .tiles--3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .home-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 700px) {
  .tiles,
  .tiles--3 {
    grid-template-columns: 1fr;
  }
  .tile {
    min-height: 140px;
  }
  .req {
    grid-template-areas:
      "title title"
      "meta meta"
      "status date";
  }
}
</style>
